import { from } from 'rxjs';
import { map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { groupBy } from 'lodash';
import {
  AssetPayload,
  AssetType,
  Entity,
  RenameRequest,
} from '@ardoq/api-types';
import { deleteAssets, renameAsset } from './actions';
import {
  collectRoutines,
  routine,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import { logError } from '@ardoq/logging';
import { alert } from '@ardoq/modal';
import { removeMetamodel } from 'streams/metamodels/metamodelActions';
import { bulkDeleteDashboard } from '../dashboards/actions';
import { viewpointDeleteSuccess } from '../../viewpoints/actions';
import { assetApi, handleError } from '@ardoq/api';
import { notifyDeletingBroadcastSucceeded } from '../../broadcasts/actions';
import { isArdoqError, Result } from '@ardoq/common-helpers';
import { showToast, ToastType } from '@ardoq/status-ui';
import { I18nLocale, t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const renameRoutine = <T>(
  namespace: string,
  renameFn: (payload: RenameRequest) => Promise<Result<T>>
) =>
  routine(
    withNamespace(namespace),
    ofType(renameAsset),
    extractPayload(),
    mergeMap(renameFn),
    handleError()
  );

const handleDeletedAssets = (assets: AssetPayload[]) => {
  const groupedAssets = groupBy(assets, ({ type }) => type);

  // groupedDeletedItems[AssetType.METAMODEL]
  (groupedAssets[AssetType.METAMODEL] || []).forEach(({ _id }) =>
    dispatchAction(removeMetamodel(_id))
  );

  const deletedDashboards = (groupedAssets[AssetType.DASHBOARD] || []).map(
    ({ _id }) => _id
  );
  dispatchAction(bulkDeleteDashboard(deletedDashboards));

  (groupedAssets[AssetType.VIEWPOINT] || []).map(({ _id }) =>
    dispatchAction(viewpointDeleteSuccess(_id))
  );

  (groupedAssets[AssetType.BROADCAST] || []).map(({ _id }) =>
    dispatchAction(notifyDeletingBroadcastSucceeded(_id))
  );
};

/**
 * The backend deletes a folder only once it has been emptied of all its
 * content. If the folder still holds assets the current user isn't allowed to
 * delete, the backend silently deletes what it can and leaves the folder in
 * place — it returns 200 with the folder absent from the deleted-assets
 * response rather than a hard error. So a requested folder that's missing from
 * the response means exactly one thing: the folder couldn't be removed because
 * it contains something the user doesn't have access to.
 */
const notifyFoldersHoldingInaccessibleContent = (
  requestedAssets: AssetPayload[],
  deletedAssets: Entity[],
  i18nLocale: I18nLocale
): void => {
  const deletedIds = new Set(deletedAssets.map(({ _id }) => _id));
  const undeletedFolderCount = requestedAssets.filter(
    ({ _id, type }) => type === AssetType.FOLDER && !deletedIds.has(_id)
  ).length;

  if (undeletedFolderCount === 0) return;

  showToast(
    undeletedFolderCount === 1
      ? t(
          i18nLocale,
          'assetsBrowser:delete.folderHoldsInaccessibleContentSingular',
          "Couldn't delete the folder because it contains items you don't have access to."
        )
      : t(
          i18nLocale,
          'assetsBrowser:delete.folderHoldsInaccessibleContentPlural',
          "Couldn't delete some folders because they contain items you don't have access to."
        ),
    ToastType.ERROR
  );
};

const handleDeleteAssets = routine(
  ofType(deleteAssets),
  extractPayload(),
  mergeMap(({ assets }) =>
    from(assetApi.deleteAssets(assets)).pipe(
      map(result =>
        isArdoqError(result)
          ? result
          : { requestedAssets: assets, deletedAssets: result }
      )
    )
  ),
  handleError(error => {
    logError(error, 'Group assets deletion failed');
    alert({
      title: 'Error',
      subtitle: 'Sorry, assets deletion failed',
    });
  }),
  withLatestFrom(i18nLocale$),
  tap(([{ requestedAssets, deletedAssets }, i18nLocale]) =>
    notifyFoldersHoldingInaccessibleContent(
      requestedAssets,
      deletedAssets,
      i18nLocale
    )
  ),
  map(([{ deletedAssets }]) =>
    deletedAssets.map(({ _id, ardoq }) => ({
      _id,
      type: ardoq['entity-type'],
    }))
  ),
  tap(handleDeletedAssets)
);

export const assetsRoutines = collectRoutines(handleDeleteAssets);
